import {
  createRouter,
  createWebHistory,
  createMemoryHistory,
} from "vue-router";
import wheelPage from "@pages/wheelPage.vue";
const exportPage = () => import("@pages/exportPage.vue");
const faqPage = () => import("@pages/faqPage.vue");
const blogPage = () => import("@pages/blogPage.vue");
const blogPostPage = () => import("@pages/blogPostPage.vue");
const useCasePage = () => import("@pages/useCasePage.vue");
const aboutPage = () => import("@pages/aboutPage.vue");
const privacyPolicyPage = () => import("@pages/privacyPolicyPage.vue");
const termsOfServicePage = () => import("@pages/termsOfServicePage.vue");
const translationsPage = () => import("@pages/translationsPage.vue");
const wheelReviewPage = () => import("@pages/wheelReviewPage.vue");
const carouselPage = () => import("@pages/carouselPage.vue");
const notFoundPage = () => import("@pages/notFoundPage.vue");
import { setLang } from "@/i18n-setup.js";
import * as Locales from "@utils/Locales.js";

// Debug function to log route changes
const logRouteChanges = (to, from) => {};

const parseQuery = (query) => {
  return new URLSearchParams(query);
};

export const routes = [
  // Root path route (homepage)
  {
    path: "/",
    name: "home",
    component: wheelPage,
  },
  {
    path: "/embed",
    name: "embed",
    component: wheelPage,
    meta: { embed: true },
  },
  {
    path: "/faq",
    component: faqPage,
    meta: {
      title: "Frequently Asked Questions | Spin the Names",
      description:
        "Find answers to common questions about using the Spin the Names wheel spinner tool.",
    },
  },
  {
    path: "/faq/:section?",
    name: "faq-section",
    component: faqPage,
    meta: {
      title: "Frequently Asked Questions | Spin the Names",
      description:
        "Find answers to common questions about using the Spin the Names wheel spinner tool.",
    },
  },
  {
    path: "/:lang([a-z][a-z])/:sharedWheelPath(\\w\\w\\w\\-\\w\\w\\w)?",
    name: "wheel-page-lang2-shared",
    component: wheelPage,
  },
  {
    path: "/:lang([a-z][a-z])",
    name: "wheel-page-lang2",
    component: wheelPage,
  },
  {
    path: "/:lang([a-z][a-z]-[A-Z][A-Z])?/:sharedWheelPath(\\w\\w\\w-\\w\\w\\w)?",
    component: wheelPage,
  },
  {
    // Three-letter lang codes don't work with "?" (optional marker).
    path: "/:lang([a-z][a-z][a-z])",
    name: "wheel-page-lang3",
    component: wheelPage,
  },
  {
    // Three-letter lang codes don't work with "?" (optional marker).
    path: "/:lang([a-z][a-z][a-z])/:sharedWheelPath(\\w\\w\\w-\\w\\w\\w)?",
    component: wheelPage,
  },
  {
    path: "/view/:lang([a-z][a-z])?/:sharedWheelPath(\\w\\w\\w-\\w\\w\\w)",
    name: "view-wheel-lang2",
    component: wheelPage,
  },
  {
    path: "/view/:lang([a-z][a-z]-[A-Z][A-Z])?/:sharedWheelPath(\\w\\w\\w-\\w\\w\\w)",
    component: wheelPage,
  },
  {
    // Three-letter lang codes don't work with "?" (optional marker).
    path: "/view/:lang([a-z][a-z][a-z])/:sharedWheelPath(\\w\\w\\w-\\w\\w\\w)",
    component: wheelPage,
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: privacyPolicyPage,
    meta: {
      title: "Privacy Policy | Spin the Names",
      description:
        "Our privacy policy explains how we collect, use, and protect your personal information when you use Spin the Names.",
    },
  },
  {
    path: "/terms-of-service",
    name: "terms-of-service",
    component: termsOfServicePage,
    meta: {
      title: "Terms of Service | Spin the Names",
      description:
        "Our terms of service outline the rules and guidelines for using the Spin the Names website and tools.",
    },
  },
  {
    path: "/export",
    component: exportPage,
    meta: {
      title: "Export Data | Spin the Names",
      description:
        "Export your wheel data to use in other applications or to save for future reference.",
    },
  },
  {
    path: "/translate",
    name: "translate",
    component: translationsPage,
    meta: {
      title: "Help Translate | Spin the Names",
      description:
        "Contribute to Spin the Names by helping translate the application into your language.",
    },
  },
  {
    path: "/admin",
    name: "admin",
    component: wheelReviewPage,
    meta: { adminOnly: true },
  },
  {
    path: "/carousel",
    component: carouselPage,
    meta: { adminOnly: true },
  },
  {
    path: "/about",
    name: "about",
    component: aboutPage,
  },
  {
    path: "/blog",
    name: "blog",
    component: blogPage,
    meta: {
      title: "Blog | Spin the Names",
      description:
        "Tips, guides, and ideas for using Spin the Names in your classroom, events, live streams, and more.",
    },
  },
  {
    path: "/blog/:year/:slug",
    name: "blog-post",
    component: blogPostPage,
  },
  {
    path: "/use-cases/:slug",
    name: "use-case",
    component: useCasePage,
  },
  // Catch-all route for 404 errors
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: notFoundPage,
    beforeEnter: (to, from) => {},
  },
];

export function createAppRouter() {
  const history = import.meta.env.SSR
    ? createMemoryHistory()
    : createWebHistory();
  const router = createRouter({
    history,
    ...getRouterOptions(),
  });
  applyRouterEnhancements(router);
  return router;
}

export function getRouterOptions() {
  return {
    parseQuery,
    routes,
  };
}

export function applyRouterEnhancements(router) {
  router.beforeEach(async (to, from) => {
    // Log route changes for debugging
    logRouteChanges(to, from);

    // Ensure i18n locale matches route or domain before each navigation
    try {
      const paramLocale =
        to.params && to.params.lang ? String(to.params.lang) : null;
      const derivedLocale =
        typeof location !== "undefined"
          ? Locales.getLocale(location.hostname, to.path)
          : null;
      const targetLocale = paramLocale || derivedLocale || "en";
      await setLang(targetLocale);
    } catch (e) {
      // Non-fatal: fall back to existing locale
      console.warn("i18n setLang failed in router.beforeEach", e);
    }

    // Check if route requires admin access
    const requiresAdmin = to.matched.some((record) => record.meta.adminOnly);

    if (requiresAdmin) {
      // Import stores dynamically to avoid circular dependency
      const { useUserStore } = await import("@/stores/useUserStore");
      const { useAdminStore } = await import("@/stores/useAdminStore");

      const userStore = useUserStore();
      const adminStore = useAdminStore();

      // Ensure user store is initialized before checking admin status
      // This is critical for direct URL navigation (typing URL in address bar)
      if (!userStore.isInitialized) {
        await userStore.initialize();
      }

      // Use the admin store to check for redirect
      const redirectInfo = await adminStore.checkAdminRedirect();

      if (redirectInfo) {
        return redirectInfo;
      }
    }

    // SECURITY NOTE: Real admin security is enforced server-side via:
    // 1. Cloud Functions with auth.requireAdmin middleware
    // 2. Firestore security rules requiring admin status
    // Client-side route guards provide UX improvement but are not relied upon for security

    // Non-admin routes allowed
    return true;
  });

  // Add afterEach hook to scroll to top
  router.afterEach((to, from) => {
    // Scroll to top when navigating
    if (
      typeof window !== "undefined" &&
      typeof window.scrollTo === "function"
    ) {
      window.scrollTo(0, 0);
    }
  });
}

export default createAppRouter;
